import {
  Badge,
  Icon,
  NavigationButton,
  Timestamp,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { Timer } from 'lib-shared-legacy-components';
import {
  conversationChannelsToCheckForThreshold,
  QueueThreshold,
} from 'lib-shared-legacy-types';
import { useNavigate } from 'react-router-dom';

import { channelAttributes } from 'utils/channels';

import { DashboardQueue } from '../../../../api/types';

import styles from './name.module.scss';

interface NameProps {
  dashboardQueue: DashboardQueue;
}

export const Name = (props: NameProps): JSX.Element => {
  const navigate = useNavigate();

  const {
    available_agents,
    breached_thresholds,
    conversations_in_queue,
    longest_wait,
    queue,
  } = props.dashboardQueue;
  const { id, name } = queue;

  const breachedThresholdMessages = Object.keys(breached_thresholds)
    .map((type) => {
      const thresholdValue: number = breached_thresholds[type];

      if (type === QueueThreshold.AvailableAgents) {
        const breachingThresholdChannels =
          conversationChannelsToCheckForThreshold.filter(
            (channel) => (available_agents[channel] ?? 0) < thresholdValue,
          );

        if (breachingThresholdChannels.length) {
          return (
            <>
              Available agents (limit {thresholdValue}):
              <ul>
                {breachingThresholdChannels.map((channel) => (
                  <li key={channel}>
                    {channelAttributes[channel]?.name}:{' '}
                    <strong>{available_agents[channel] ?? 0}</strong>
                  </li>
                ))}
              </ul>
            </>
          );
        }
      }

      if (type === QueueThreshold.LongestWait && longest_wait) {
        return (
          <>
            Current longest wait:{' '}
            <strong>
              <Timestamp
                counter={true}
                refreshRate={1000}
                time={longest_wait}
              />
            </strong>{' '}
            (limit {<Timer asCounter startAt={thresholdValue} start={false} />})
          </>
        );
      }

      if (
        type === QueueThreshold.WaitingConversations &&
        conversations_in_queue
      ) {
        return (
          <>
            Waiting conversations: <strong>{conversations_in_queue}</strong>{' '}
            (limit {thresholdValue})
          </>
        );
      }

      return null;
    })
    .filter(Boolean) as readonly JSX.Element[];

  const thresholdWarningTooltipContent = !!breachedThresholdMessages.length && (
    <ul className={styles.thresholdWarnings}>
      {breachedThresholdMessages.map((message, index) => (
        <li key={index} className={styles.thresholdWarning}>
          {message}
        </li>
      ))}
    </ul>
  );

  return (
    <div className={styles.container}>
      <div className={styles.nameLink}>
        <Tooltip content={name} dark>
          <NavigationButton
            navigate={() => navigate(`/conversations?queue=${id}&status=open`)}
          >
            {name}
          </NavigationButton>
        </Tooltip>
      </div>

      {!!breachedThresholdMessages.length && (
        <Tooltip
          head="Threshold alerts"
          content={thresholdWarningTooltipContent}
          side="right"
          wrap="div"
          wrapProps={{ className: styles.icon }}
          light
        >
          <Icon name="exclamation" />
        </Tooltip>
      )}

      {queue.is_archived && (
        <Badge variant="error" size="small">
          Archived
        </Badge>
      )}
    </div>
  );
};
